import { useVoiceOptions } from '@startupquickstart/frontend';
import { AiGenerateDialog } from '@/components/ai-job/AiGenerateDialog';
import { AudioLines } from 'lucide-react';
import { useEffect, useState } from 'react';

type VoiceoverType = 'both' | 'description' | 'long_description';

interface GenerateVoiceoversActionProps {
  attractionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultVoiceoverType?: VoiceoverType;
}

export function GenerateVoiceoversAction({
  attractionId,
  open,
  onOpenChange,
  defaultVoiceoverType,
}: GenerateVoiceoversActionProps) {
  const { voices, isLoading: voicesLoading } = useVoiceOptions();
  const [voiceoverType, setVoiceoverType] = useState<VoiceoverType>(defaultVoiceoverType ?? 'both');
  const [voice, setVoice] = useState<string>('alloy');

  useEffect(() => {
    if (open) {
      setVoiceoverType(defaultVoiceoverType ?? 'both');
    }
  }, [open, defaultVoiceoverType]);

  useEffect(() => {
    if (voices.length === 0) return;
    if (!voices.some((v) => v.value === voice)) {
      setVoice(voices[0].value);
    }
  }, [voices, voice]);

  return (
    <AiGenerateDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Generate Voiceovers"
      description="Generate voiceovers for this attraction using the short or long description."
      endpoint={`/attractions/${attractionId}/generate-voiceovers`}
      invalidateKeys={[['Attraction', attractionId]]}
      successMessage="Voiceovers generated successfully"
      errorFallback="Failed to generate voiceovers"
      buildPayload={() => ({ voiceoverType, voice })}
    >
      <div>
        <label htmlFor="voiceoverType" className="block text-sm font-medium mb-1">
          Voiceover Type <span className="text-destructive">*</span>
        </label>
        <select
          id="voiceoverType"
          required
          className="w-full px-3 py-2 border rounded-md"
          value={voiceoverType}
          onChange={(e) => setVoiceoverType(e.target.value as VoiceoverType)}
        >
          <option value="both">Both</option>
          <option value="description">Short Description</option>
          <option value="long_description">Long Description</option>
        </select>
      </div>

      <div>
        <label htmlFor="voice" className="block text-sm font-medium mb-1">
          Voice <span className="text-destructive">*</span>
        </label>
        <select
          id="voice"
          required
          disabled={voicesLoading}
          className="w-full px-3 py-2 border rounded-md"
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
        >
          {voices.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </AiGenerateDialog>
  );
}

export const GenerateVoiceoversIcon = AudioLines;
